import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from 'react-native';
import { ActivityNote } from '@/experiences/sales-portal/components/activity-notes/types';
import {
  Badge,
  Icons,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { DetailSectionEmptyState } from '@/experiences/sales-portal/components/detail-section-empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import {
  formatNoteMetadata,
  formatNoteReminder,
  formatVisitPills,
  NOTE_MAX_LINES,
  useActivityNoteStyles,
} from '@/experiences/sales-portal/components/activity-notes/utils';

interface ActivityNotesMobileProps {
  activityNotes: ActivityNote[];
  hasError: boolean;
  loading: boolean;
  onRetry: () => void;
}

export const ActivityNotesMobile: React.FC<ActivityNotesMobileProps> = ({
  activityNotes,
  hasError,
  loading,
  onRetry,
}) => {
  const [styles] = useActivityNoteStyles();

  return (
    <YStack>
      <YStack style={styles.sectionHeader}>
        <Typography accessibilityRole="header" size="$lg" variant="heading">
          {i18n.t('sales_portal.activity_notes.title')}
        </Typography>
      </YStack>
      {hasError && activityNotes.length === 0 ? (
        <ErrorState
          description={i18n.t('sales_portal.activity_notes.error')}
          onPress={onRetry}
          title={i18n.t('sales_portal.error.title')}
        />
      ) : activityNotes.length === 0 ? (
        <DetailSectionEmptyState
          text={i18n.t('sales_portal.activity_notes.empty_state')}
        />
      ) : (
        <YStack style={styles.notesList}>
          {activityNotes.map((note) => {
            const reminder = formatNoteReminder(note);
            const pills = formatVisitPills(note);

            return (
              <YStack key={note.uuid} style={styles.card}>
                <Typography numberOfLines={NOTE_MAX_LINES} size="$md">
                  {note.content}
                </Typography>
                <Typography color="$textSecondary" size="$sm">
                  {formatNoteMetadata(note)}
                </Typography>
                {pills.length > 0 || reminder ? (
                  <XStack style={styles.pillRow}>
                    {reminder ? (
                      <XStack
                        accessibilityLabel={reminder.accessibilityLabel}
                        accessible
                      >
                        <Badge color="info" icon={<Icons.Bell />} size="$lg">
                          {reminder.text}
                        </Badge>
                      </XStack>
                    ) : null}
                    {pills.map((pill) => (
                      <Badge color="muted" key={pill.key} size="$lg">
                        {pill.label}
                      </Badge>
                    ))}
                  </XStack>
                ) : null}
              </YStack>
            );
          })}
          {loading ? (
            <ActivityIndicator style={styles.loadingIndicator} />
          ) : null}
        </YStack>
      )}
    </YStack>
  );
};
